import { X, Upload, Trash2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useUploadFile } from 'features/seller/hooks/useUploadFile';

const DOCUMENT_TYPES = [
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'pan', label: 'PAN' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'gst', label: 'GST' },
  { value: 'trade_licence', label: 'Trade Licence' },
  { value: 'fssai', label: 'FSSAI' },
  { value: 'tourism_licence', label: 'Tourism Licence' },
];

const VERIFICATION_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

const EMPTY_FORM = {
  type: '',
  documentNumber: '',
  verificationStatus: 'pending',
  rejectionReason: '',
};

// existingDoc shape: { _id, type, documentNumber, documentUrl: [...keys], verificationStatus, rejectionReason }
const KycDocumentModal = ({ isOpen, onClose, onSubmit, existingDoc = null, isSubmitting }) => {
  const isEditMode = Boolean(existingDoc);

  const [form, setForm] = useState(EMPTY_FORM);
  // Each entry: { key: string, preSignedUrl: string } — mirrors your upload pattern
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const { mutate: uploadFile, isPending: isUploading } = useUploadFile();

  // Pre-fill in edit mode
  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && existingDoc) {
      setForm({
        type: existingDoc.type || '',
        documentNumber: existingDoc.documentNumber || '',
        verificationStatus: existingDoc.verificationStatus || 'pending',
        rejectionReason: existingDoc.rejectionReason || '',
      });

      // documentUrl from DB comes as raw S3 keys — normalise to { key, preSignedUrl }
      // preSignedUrl may not be available here, so we use the key as a display fallback.
      // If your seller fetch already returns preSignedUrls, pass them through existingDoc.documentPreSignedUrls.
      const existingUrls = Array.isArray(existingDoc.documentUrl)
        ? existingDoc.documentUrl
        : existingDoc.documentUrl
        ? [existingDoc.documentUrl]
        : [];

      setUploadedFiles(
        existingUrls.map((entry) => {
          // entry may already be { key, preSignedUrl } if you normalised upstream
          if (typeof entry === 'object' && entry.key) return entry;
          return { key: entry, preSignedUrl: entry }; // fallback: use key as URL
        })
      );
    } else {
      setForm(EMPTY_FORM);
      setUploadedFiles([]);
    }
  }, [isOpen, existingDoc, isEditMode]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      uploadFile(file, {
  onSuccess: (data) => {
    setUploadedFiles((prev) => [
      ...prev,
      { key: data.data.key, preSignedUrl: data.data.preSignedUrl },
    ]);
  },
});
    });

    // Reset input so same file can be re-selected if needed
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isFormValid =
    form.type &&
    uploadedFiles.length > 0 &&
    (form.verificationStatus !== 'rejected' || form.rejectionReason.trim());

  const handleSubmit = () => {
    if (!isFormValid) return;

    onSubmit({
      type: form.type,
      documentNumber: form.documentNumber.trim() || undefined,
      documentUrl: uploadedFiles.map((f) => f.key), // send only keys to backend
      verificationStatus: form.verificationStatus,
      rejectionReason:
        form.verificationStatus === 'rejected' ? form.rejectionReason.trim() : undefined,
    });
  };

  const getFilename = (key) => key?.split('/').pop() || key;

  const isImageKey = (key) => /\.(jpg|jpeg|png|gif|webp)$/i.test(key?.split('?')[0]);

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'>
      <div className='bg-card border border-border rounded-lg shadow-lg w-full max-w-lg flex flex-col max-h-[90vh]'>
        {/* Header */}
        <div className='flex items-center justify-between p-5 border-b border-border shrink-0'>
          <h3 className='text-base font-semibold text-foreground'>
            {isEditMode ? 'Edit KYC Document' : 'Add KYC Document'}
          </h3>
          <button
            onClick={onClose}
            disabled={isSubmitting || isUploading}
            className='text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50'
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className='overflow-y-auto p-5 space-y-4 flex-1'>
          {/* Document Type */}
          <div>
            <label className='block text-xs font-medium text-foreground mb-1'>
              Document Type <span className='text-red-500'>*</span>
            </label>
            <select
              value={form.type}
              onChange={(e) => handleFieldChange('type', e.target.value)}
              className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
            >
              <option value=''>Select type</option>
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Document Number */}
          <div>
            <label className='block text-xs font-medium text-foreground mb-1'>
              Document Number
            </label>
            <input
              type='text'
              value={form.documentNumber}
              onChange={(e) => handleFieldChange('documentNumber', e.target.value)}
              placeholder='e.g. ABCDE1234F'
              className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
            />
          </div>

          {/* Verification Status */}
          <div>
            <label className='block text-xs font-medium text-foreground mb-1'>
              Verification Status
            </label>
            <select
              value={form.verificationStatus}
              onChange={(e) => handleFieldChange('verificationStatus', e.target.value)}
              className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
            >
              {VERIFICATION_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Rejection Reason — only when rejected */}
          {form.verificationStatus === 'rejected' && (
            <div>
              <label className='block text-xs font-medium text-foreground mb-1'>
                Rejection Reason <span className='text-red-500'>*</span>
              </label>
              <textarea
                value={form.rejectionReason}
                onChange={(e) => handleFieldChange('rejectionReason', e.target.value)}
                rows={2}
                placeholder='Enter reason for rejection...'
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none'
              />
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className='block text-xs font-medium text-foreground mb-1'>
              Documents <span className='text-red-500'>*</span>
            </label>

            {/* Uploaded file previews */}
            {uploadedFiles.length > 0 && (
              <div className='space-y-2 mb-3'>
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className='flex items-center gap-3 p-2 border border-border rounded-md bg-muted/30'
                  >
                    {isImageKey(file.key) ? (
                      <img
                        src={file.preSignedUrl}
                        alt={`doc-${index}`}
                        className='w-10 h-10 object-cover rounded border border-border shrink-0'
                      />
                    ) : (
                      <div className='w-10 h-10 flex items-center justify-center bg-muted rounded border border-border shrink-0'>
                        <span className='text-xs text-muted-foreground font-medium'>PDF</span>
                      </div>
                    )}
                    <span className='text-xs text-foreground flex-1 truncate'>
                      {getFilename(file.key)}
                    </span>
                    <button
                      type='button'
                      onClick={() => handleRemoveFile(index)}
                      className='text-red-500 hover:text-red-700 transition-colors shrink-0'
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              accept='image/*,.pdf'
              multiple
              className='hidden'
            />
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className='flex items-center gap-2 px-4 py-2 text-sm border border-dashed border-border rounded-md bg-muted/20 hover:bg-muted/40 transition-colors disabled:opacity-50 w-full justify-center'
            >
              <Upload size={14} />
              {isUploading ? 'Uploading...' : 'Upload Files'}
            </button>
            <p className='text-xs text-muted-foreground mt-1'>
              Accepted: images, PDF. Multiple files allowed.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className='flex justify-end gap-3 p-5 border-t border-border shrink-0'>
          <button
            type='button'
            onClick={onClose}
            disabled={isSubmitting || isUploading}
            className='px-4 py-2 text-sm border border-border rounded-md bg-card hover:bg-accent transition-colors disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting || isUploading}
            className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {isSubmitting ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Document'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KycDocumentModal;